import React from "react";
import { Select, Typography } from "antd";
import { useWizard } from "../WizardContext";
import { WIZARD_STEPS, type StepKey } from "../wizardSteps";

/**
 * The steps whose content is read from one server: channels and banks hang off
 * a server, and the adapters, modifiers, lifts, debounce overrides and BACnet
 * configuration shown are the ones those banks reference. The remaining steps
 * after Server (users, notifications, actions, monitor grid, review) belong to
 * the organization as a whole.
 */
const SERVER_SCOPED_STEPS: readonly StepKey[] = [
  "Adapters",
  "Channels",
  "Banks",
  "ChannelModifiers",
  "Lifts",
  "Debounce",
  "Bacnet",
];

const SERVER_STEP_INDEX = WIZARD_STEPS.findIndex((step) => step.key === "Server");

/**
 * Whether the selector belongs on `step`: every step after Server, so the
 * server chosen there stays in front of the operator for the rest of the
 * wizard. The Server step itself manages the servers and does not take it.
 */
export function showsServerSelect(step: StepKey): boolean {
  return WIZARD_STEPS.findIndex((candidate) => candidate.key === step) > SERVER_STEP_INDEX;
}

/** Whether `step` shows data read from the selected server, as opposed to the organization's. */
function isServerScopedStep(step: StepKey): boolean {
  return SERVER_SCOPED_STEPS.includes(step);
}

/**
 * Picks which of the organization's servers the wizard is configuring. Rendered
 * once above the step content on every step after Server, so the choice is one
 * value for the whole wizard: Channels can never show one server while Banks
 * shows another, and a step opens on the server the previous one was using.
 * Hidden when the organization has a single server, since there is nothing to
 * choose. On the organization-wide steps it says so, because what they list
 * does not change with the server.
 */
function WizardServerSelect({ step }: { step: StepKey }) {
  const { state, serverOptions, selectServer, isHydrating } = useWizard();

  if (serverOptions.length < 2) return null;

  return (
    <div
      data-testid="wizard-server-select"
      style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}
    >
      <Typography.Text strong>Server</Typography.Text>
      <Select
        data-testid="wizard-server-select-input"
        aria-label="Server"
        style={{ minWidth: 260 }}
        value={state.serverId ?? undefined}
        disabled={isHydrating}
        showSearch
        optionFilterProp="label"
        options={serverOptions.map((option) => ({ value: option.id, label: option.name }))}
        onChange={(serverId: string) => selectServer(serverId)}
      />
      {!isServerScopedStep(step) && (
        <Typography.Text type="secondary" data-testid="wizard-server-select-org-wide">
          This step covers the whole organization, not just this server.
        </Typography.Text>
      )}
    </div>
  );
}

export default WizardServerSelect;
